import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Box,
  Divider,
} from "@mui/material";

const subjectIcons = {
  // Math
  math: "➗",
  maths: "➗",
  mathematics: "➗",

  // Science
  science: "🔬",

  // Social Science
  "social science": "🏛️",
  "social studies": "🏛️",

  // Physics
  physics: "⚛️",

  // Chemistry
  chemistry: "🧪",
  chem: "🧪",

  // Biology
  biology: "🦠",
  bio: "🦠",

  // English
  english: "📚",
  eng: "📚",

  // Hindi
  hindi: "📖",

  // Geography
  geography: "🌍",
  geo: "🌍",

  // History
  history: "🏛️",

  // Computer
  computer: "💻",
  comp: "💻",
  cs: "💻",

  // Default
  default: "📂",
};

const SubjectsPage = () => {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchSubjects = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/Student/GetStudentSubject",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({
            studentId: sessionStorage.getItem("employeeId"),
            classId: sessionStorage.getItem("classId") || 0,
            sectionId: sessionStorage.getItem("sectionId") || 0,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Error fetching subjects: ${response.status}`);
      }

      const data = await response.json();
      setSubjects(Array.isArray(data) ? data : []);
    } catch (error) {
      setError("Failed to load subjects. Please try again later.");
      console.error("Fetch subjects error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, []);

  const getSubjectIcon = (subjectName) => {
    const lowerSubject = subjectName.toLowerCase();
    for (const [key, icon] of Object.entries(subjectIcons)) {
      if (lowerSubject.includes(key)) {
        return icon;
      }
    }
    return subjectIcons.default;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold", color: "#333" }}>
        Your Subjects
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {subjects.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No subjects available at the moment. Check back later!
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {subjects.map((subject) => (
            <Grid item xs={12} sm={6} md={4} key={subject.subjectId}>
              <Card
                onClick={() => navigate(`/lessons/${subject.classId}/${subject.subjectId}`)}
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    p: 3,
                  }}
                >
                  <Box sx={{ fontSize: "48px", mb: 2, color: "#4596a4" }}>
                    {getSubjectIcon(subject.subjectName)}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333" }}>
                    {subject.subjectName}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default SubjectsPage;
